import { useState } from "react";
import { useAuth } from "../lib/AuthContext";

const COLORS = {
  bg: "#F8F7F4",
  surface: "#FFFFFF",
  surfaceAlt: "#F1F0EC",
  border: "#E2E0DA",
  text: "#1A1917",
  textSecondary: "#6B6860",
  textMuted: "#9C9889",
  accent: "#2B5CE6",
  accentLight: "#EBF0FD",
  red: "#C53030",
  redLight: "#FEE7E7",
  green: "#1A8754",
  greenLight: "#E8F5EF",
};

export default function LoginPage() {
  const { signIn, signUp, registerRecruiter, resetPassword, sendMagicLink } = useAuth();
  // tab: "magic" (default) | "password" | "signup" | "reset"
  const [tab, setTab] = useState("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const switchTab = (next) => {
    setTab(next);
    setError("");
    setSuccess("");
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) return setError("Enter your email.");
    setSubmitting(true);
    try {
      await sendMagicLink(email);
      setSuccess(
        `We sent a sign-in link to ${email}. Open it on this device — valid for one hour.`
      );
    } catch (err) {
      const msg = err?.message || "";
      // Supabase returns this when shouldCreateUser:false and the account doesn't exist.
      if (/Signups not allowed|user not found|invalid/i.test(msg)) {
        setError(
          "We couldn't find an account for that email. New here? Switch to Sign Up to create one."
        );
      } else {
        setError(msg || "Could not send sign-in link. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email || !password) return setError("Please fill in all fields.");
    setSubmitting(true);
    try {
      await signIn(email, password);
      // AuthContext handles the rest
    } catch (err) {
      setError(err.message || "Login failed. Check your credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email || !password || !confirmPassword || !fullName || !companyName) {
      return setError("Please fill in all fields.");
    }
    if (password !== confirmPassword) return setError("Passwords don't match.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setSubmitting(true);
    try {
      const { session } = await signUp(email, password);
      if (session?.access_token) {
        await registerRecruiter(fullName, companyName, session.access_token);
      }
      setSuccess("Account created! Check your email to confirm, then sign in.");
      switchTab("magic");
    } catch (err) {
      setError(err.message || "Signup failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) return setError("Enter your email.");
    setSubmitting(true);
    try {
      await resetPassword(email);
    } finally {
      // Don't reveal whether the account exists.
      setSuccess("If an account exists for that email, you'll receive a reset link shortly.");
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
    marginBottom: 6,
  };

  const primaryButtonStyle = {
    width: "100%",
    padding: "11px 0",
    borderRadius: 8,
    border: "none",
    backgroundColor: COLORS.accent,
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: submitting ? "wait" : "pointer",
    opacity: submitting ? 0.7 : 1,
    transition: "opacity 0.15s",
  };

  const linkButtonStyle = {
    background: "none",
    border: "none",
    color: COLORS.accent,
    fontSize: 13,
    fontFamily: "inherit",
    cursor: "pointer",
    fontWeight: 500,
  };

  const onSignInTab = tab === "magic" || tab === "password" || tab === "reset";

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 20,
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 12,
          }}>R</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Relay
          </h1>
          <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
            Recruiter Dashboard
          </p>
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 28,
        }}>
          {/* Top-level tabs: Sign In / Sign Up */}
          <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
            <button onClick={() => switchTab("magic")} style={{
              flex: 1, padding: "8px 0", borderRadius: 6, border: "none", fontSize: 14, fontWeight: 600,
              fontFamily: "inherit", cursor: "pointer", transition: "all 0.15s",
              backgroundColor: onSignInTab ? COLORS.accent : COLORS.surfaceAlt,
              color: onSignInTab ? "#fff" : COLORS.textSecondary,
            }}>
              Sign In
            </button>
            <button onClick={() => switchTab("signup")} style={{
              flex: 1, padding: "8px 0", borderRadius: 6, border: "none", fontSize: 14, fontWeight: 600,
              fontFamily: "inherit", cursor: "pointer", transition: "all 0.15s",
              backgroundColor: tab === "signup" ? COLORS.accent : COLORS.surfaceAlt,
              color: tab === "signup" ? "#fff" : COLORS.textSecondary,
            }}>
              Sign Up
            </button>
          </div>

          {/* Error / Success messages */}
          {error && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 13,
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 16,
              backgroundColor: COLORS.greenLight, color: COLORS.green, fontSize: 13,
            }}>{success}</div>
          )}

          {tab === "magic" && (
            <form onSubmit={handleMagicLink}>
              <p style={{
                fontSize: 13, color: COLORS.textSecondary, margin: "0 0 18px", lineHeight: 1.5,
              }}>
                Sign in with a one-time link sent to your email. No password needed.
              </p>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" style={inputStyle} autoComplete="email" autoFocus />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Sending..." : "Send sign-in link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchTab("password")} style={linkButtonStyle}>
                  Use password instead
                </button>
              </div>
            </form>
          )}

          {tab === "password" && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" style={inputStyle} autoComplete="email" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Your password" style={inputStyle} autoComplete="current-password" />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Logging in..." : "Log In"}
              </button>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14,
              }}>
                <button type="button" onClick={() => switchTab("magic")} style={linkButtonStyle}>
                  Use email link instead
                </button>
                <button type="button" onClick={() => switchTab("reset")} style={linkButtonStyle}>
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {tab === "signup" && (
            <form onSubmit={handleSignUp}>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Full Name</label>
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
                  placeholder="Jane Doe" style={inputStyle} autoComplete="name" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Company Name</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
                  placeholder="Acme Corp" style={inputStyle} autoComplete="organization" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" style={inputStyle} autoComplete="email" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters" style={inputStyle} autoComplete="new-password" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password" style={inputStyle} autoComplete="new-password" />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Creating account..." : "Create Account"}
              </button>
              <p style={{ textAlign: "center", fontSize: 12, color: COLORS.textMuted, marginTop: 14 }}>
                After signing up, a careers team member will review and approve your account.
              </p>
            </form>
          )}

          {tab === "reset" && (
            <form onSubmit={handleReset}>
              <p style={{
                fontSize: 13, color: COLORS.textSecondary, margin: "0 0 18px", lineHeight: 1.5,
              }}>
                We'll email you a link to set a new password.
              </p>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" style={inputStyle} autoComplete="email" />
              </div>
              <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                {submitting ? "Sending..." : "Send reset link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={() => switchTab("magic")} style={linkButtonStyle}>
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: COLORS.textMuted, marginTop: 20 }}>
          Powered by Relay &mdash; The AI Early Talent Engine by Careersorter
        </p>
      </div>
    </div>
  );
}
